/**
 * Local experiment idea backlog (Ideate stage).
 * Persists until a server-side backlog lands.
 */

export type ExperimentIdea = {
  id: string
  propertyId: string
  title: string
  hypothesis: string
  pageUrl?: string
  source: "manual" | "ai" | "rules" | "learning"
  impact?: number
  confidence?: number
  ease?: number
  createdAt: string
  promotedAt?: string
}

const STORAGE_KEY = "humblytics:experiment:ideas:v1"

function readAll(): ExperimentIdea[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as ExperimentIdea[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeAll(ideas: ExperimentIdea[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ideas.slice(0, 300)))
  } catch {
    /* ignore */
  }
}

export function listExperimentIdeas(propertyId?: string): ExperimentIdea[] {
  const all = readAll().sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
  if (!propertyId) return all
  return all.filter((i) => i.propertyId === propertyId)
}

export function addExperimentIdea(
  idea: Omit<ExperimentIdea, "id" | "createdAt"> & { id?: string },
): ExperimentIdea {
  const entry: ExperimentIdea = {
    ...idea,
    id: idea.id || crypto.randomUUID(),
    createdAt: new Date().toISOString(),
  }
  writeAll([entry, ...readAll().filter((i) => i.id !== entry.id)])
  return entry
}

export function removeExperimentIdea(id: string): void {
  writeAll(readAll().filter((i) => i.id !== id))
}

export function iceScore(idea: ExperimentIdea): number {
  const i = idea.impact ?? 5
  const c = idea.confidence ?? 5
  const e = idea.ease ?? 5
  return Math.round(((i + c + e) / 3) * 10) / 10
}
